import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { api, errMsg, usePoll } from "../lib/api";
import { useData } from "../lib/DataContext";
import { PageHeader, Panel } from "../components/shared";
import ItemFactSheet from "../components/ItemFactSheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { name: "", contact: "", email: "", phone: "", lead_time_days: 7, lead_override: false, notes: "" };
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const FIELDS = [["name", "NAME"], ["contact", "CONTACT"], ["email", "EMAIL"], ["phone", "PHONE"], ["lead_time_days", "MANUAL LEAD TIME (DAYS)"], ["notes", "NOTES"]];
const SRC = { measured: "MEASURED", override: "MANUAL OVERRIDE", manual: "MANUAL · NO PO HISTORY" };

function LeadStats({ s }) {
  const m = s.measured_lead || {};
  return (
    <div className="mt-4 pt-4 border-t border-[#1E293B] font-mono text-xs" data-testid={`supplier-lead-${s.id}`}>
      <div className="flex gap-6">
        <div><div className="text-[#6B7280]">LEAD (USED)</div><div className="text-lg text-[#38BDF8]" data-testid={`supplier-effective-lead-${s.id}`}>{s.effective_lead_days}d</div><div className={`text-[9px] ${s.lead_source === "override" ? "text-[#F59E0B]" : "text-[#6B7280]"}`}>{SRC[s.lead_source]}</div></div>
        <div><div className="text-[#6B7280]">ACTUAL AVG</div><div className="text-lg" data-testid={`supplier-measured-lead-${s.id}`}>{m.samples ? `${m.avg_days}d` : "—"}</div><div className="text-[9px] text-[#6B7280]">{m.samples ? `${m.samples} PO${m.samples > 1 ? "s" : ""} · ${m.min_days}–${m.max_days}d` : "SENT → RECEIVED"}</div></div>
        <div><div className="text-[#6B7280]">MANUAL</div><div className="text-lg">{s.lead_time_days}d</div></div>
      </div>
    </div>
  );
}

function Catalog({ s, linked, onOpen }) {
  const [open, setOpen] = useState(false);
  const t1 = linked.filter((i) => i.tier === 1).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="mt-3 pt-3 border-t border-[#1E293B]" data-testid={`supplier-catalog-${s.id}`}>
      <button data-testid={`supplier-catalog-toggle-${s.id}`} onClick={() => setOpen(!open)} className="w-full flex justify-between items-center font-mono text-xs h-9">
        <span><span className="text-[#6B7280]">CATALOG </span>{t1.length} material{t1.length === 1 ? "" : "s"}</span>
        <span className="text-[#F59E0B]">{open ? "HIDE ▲" : "SHOW ▼"}</span>
      </button>
      {open && (
        <div className="mt-2 max-h-80 overflow-y-auto divide-y divide-[#1E293B]">
          {t1.map((i) => {
            const pack = i.order_unit_qty || 1, cost = Number(i.unit_cost) || 0;
            return (
              <div key={i.id} data-testid={`supplier-catalog-row-${i.sku}`} role="button" tabIndex={0} onClick={() => onOpen(i.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(i.id)}
                data-tip="Open Item Fact Sheet" className="py-2 px-1 -mx-1 text-sm cursor-pointer rounded hover:bg-[#172033]">
                <div className="flex justify-between gap-2"><span className="truncate">{i.name}</span>
                  <span className="font-mono text-xs shrink-0">{cost ? `$${(cost * pack).toFixed(2)}/pack` : "no cost"}</span></div>
                <div className="flex justify-between gap-2 font-mono text-[11px] text-[#6B7280]">
                  <span>PART # <span data-testid={`supplier-catalog-ext-${i.sku}`} className={i.external_sku ? "text-[#E5E7EB]" : "text-[#C4B5FD]"}>{i.external_sku || "NOT SET"}</span> · ours {i.sku}</span>
                  <span className="shrink-0">{pack} {i.unit}/pack · ${cost.toFixed(2)}/{i.unit}</span></div>
              </div>
            );
          })}
          {!t1.length && <div className="py-3 font-mono text-[11px] text-[#6B7280]">NO RAW MATERIALS LINKED</div>}
        </div>
      )}
    </div>
  );
}

export default function Suppliers() {
  const { suppliers, items, refreshAll } = useData();
  const { data: emailCfg } = usePoll("/po-email/settings", 15000);
  const autoLive = !!emailCfg?.auto_send;
  const [fact, setFact] = useState(null);
  const [form, setForm] = useState(null);

  const save = async () => {
    const body = { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] ?? EMPTY[k]])), lead_time_days: Number(form.lead_time_days), lead_override: !!form.lead_override };
    try {
      if (form.id) await api.put(`/suppliers/${form.id}`, body); else await api.post("/suppliers", body);
      toast.success("Supplier saved"); setForm(null); refreshAll();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleAutoEmail = async (s, v) => {
    try { await api.put(`/suppliers/${s.id}`, { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] ?? EMPTY[k]])), auto_email: v }); toast.success(v ? `${s.name}: auto-email on` : `${s.name}: auto-email off`); refreshAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleOverride = async (s, v) => {
    try { await api.put(`/suppliers/${s.id}`, { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] ?? EMPTY[k]])), lead_override: v }); toast.success(v ? "Manual lead time now overrides measured" : "Using measured lead time"); refreshAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Deleted"); refreshAll(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader kicker="// ADMIN · VENDORS" title="Suppliers">
        <button data-testid="supplier-new-btn" onClick={() => setForm(EMPTY)} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2"><Plus className="w-4 h-4" />New Supplier</button>
      </PageHeader>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {suppliers.map((s) => {
          const linked = items.filter((i) => i.supplier_id === s.id);
          return (
            <Panel key={s.id} className="p-6 rise" data-testid={`supplier-card-${s.id}`}>
              <div className="flex justify-between items-start gap-2">
                <h3 className="font-display text-xl font-bold flex items-center gap-2">{s.name}{s.auto_email && !EMAIL_RX.test(s.email || "") && <AlertTriangle data-testid={`supplier-missing-email-${s.id}`} data-tip="Missing Email Address for Auto-Email" className="w-5 h-5 text-[#EF4444]" />}</h3>
                <div className="flex gap-1">
                  <button data-testid={`supplier-edit-${s.id}`} onClick={() => setForm(s)} className="h-10 w-10 flex items-center justify-center rounded hover:bg-[#172033]"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`supplier-delete-${s.id}`} onClick={() => del(s)} className="h-10 w-10 flex items-center justify-center rounded text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-sm text-[#9CA3AF] mt-2 space-y-1">
                <div>{s.contact}</div><div className="font-mono text-xs">{s.email}</div><div className="font-mono text-xs">{s.phone}</div>
              </div>
              <LeadStats s={s} />
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input type="checkbox" data-testid={`supplier-override-${s.id}`} className="w-5 h-5 accent-[#F59E0B]" checked={!!s.lead_override} onChange={(e) => toggleOverride(s, e.target.checked)} />
                Override measured lead time (use manual {s.lead_time_days}d)
              </label>
              <label className={`flex items-center gap-2 mt-2 text-sm ${autoLive ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`} data-tip={autoLive ? undefined : "Turn on Auto Send PO Emails to Suppliers in Master Controls first"}>
                <input type="checkbox" data-testid={`supplier-auto-email-${s.id}`} disabled={!autoLive} className="w-5 h-5 accent-[#F59E0B]" checked={!!s.auto_email} onChange={(e) => toggleAutoEmail(s, e.target.checked)} />
                Auto-Email Orders
              </label>
              <Catalog s={s} linked={linked} onOpen={setFact} />
            </Panel>
          );
        })}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        {form && (
          <DialogContent className="bg-[#0F1623] border-[#334155] max-w-lg" data-testid="supplier-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} Supplier</DialogTitle></DialogHeader>
            {FIELDS.map(([k, l]) => (
              <label key={k} className="flex flex-col gap-1"><span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">{l}</span>
                <input data-testid={`supplier-${k}-input`} type={k === "lead_time_days" ? "number" : "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-12 px-3 bg-[#090D16] border border-[#334155] rounded-md" /></label>
            ))}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="supplier-lead_override-input" className="w-5 h-5 accent-[#F59E0B]" checked={!!form.lead_override} onChange={(e) => setForm({ ...form, lead_override: e.target.checked })} />
              Override measured lead time with the manual value
            </label>
            <button data-testid="supplier-save-btn" disabled={!form.name} onClick={save} className="h-14 rounded-md bg-[#F59E0B] text-[#090D16] font-display font-extrabold disabled:opacity-40">SAVE</button>
          </DialogContent>
        )}
      </Dialog>
      {fact && <ItemFactSheet itemId={fact} onClose={() => setFact(null)} />}
    </div>
  );
}
